/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {firstValueFrom, Subscription} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {ModalService, ToastService, IxModule} from '@siemens/ix-angular';
import {TranslateService, TranslateModule} from '@ngx-translate/core';
import {DppService} from '../../../../services/dpp.service';
import {
  File,
  LangString,
  MultiLanguageProperty,
  Property,
  Submodel,
  SubmodelElement,
  SubmodelElementCollection,
  SubmodelElementList,
  SubmodelElementTypes,
} from '@product-passport/dpp/model';
import {getSubmodelMetadata} from '../../../../models/dpp/submodel-types';
import {LocalizationHelper} from '../../../shared/utils/localization-helper';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {SubmodelElementComponent} from './components/submodel-element/submodel-element.component';

type SubmodelFormControl = FormControl | FormGroup | FormArray;

@Component({
  selector: 'app-generic-submodel-editor',
  templateUrl: './generic-submodel-editor.component.html',
  styleUrl: './generic-submodel-editor.component.scss',
  imports: [
    IxModule,
    TranslateModule,
    ReactiveFormsModule,
    CommonModule,
    SubmodelElementComponent,
  ],
})
export class GenericSubmodelEditorComponent implements OnInit, OnDestroy {
  @ViewChild('cancelModal', {read: TemplateRef})
  cancelModalRef!: TemplateRef<HTMLElement>;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dppService = inject(DppService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly localizationHelper = inject(LocalizationHelper);
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly modalService = inject(ModalService);

  private routeSubscription?: Subscription;

  dppId = '';
  submodelId = '';
  shellName = '';

  submodel?: Submodel;
  form!: FormGroup;

  loading = true;
  saving = false;
  passFound = true;

  get submodelVersion(): string | undefined {
    const semanticId = this.submodel?.semanticId?.keys?.[0]?.value;
    return getSubmodelMetadata(semanticId)?.version;
  }

  get isCreateMode(): boolean {
    return !this.submodelId;
  }

  get submodelName(): string {
    const semanticId = this.submodel?.semanticId?.keys?.[0]?.value;
    const metadata = getSubmodelMetadata(semanticId);
    if (metadata) {
      return this.translate.instant(metadata.type);
    }
    return this.localizationHelper.getLocalizedText(
      this.submodel?.displayName,
      this.submodel?.idShort ?? this.submodelId,
    );
  }

  ngOnInit(): void {
    this.routeSubscription = this.route.params.subscribe(async (params) => {
      this.dppId = params['aasId'];
      this.submodelId = params['submodelId'];
      this.shellName =
        this.route.snapshot.queryParamMap.get('shellName') ?? this.dppId;
      await this.loadSubmodelData();
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }

  private async loadSubmodelData(): Promise<void> {
    try {
      if (this.isCreateMode) {
        this.submodel = await firstValueFrom(
          this.http.get<Submodel>('/assets/dpp/dnp_schema.json'),
        );
      } else {
        this.submodel = await this.dppService.getSubmodel(this.submodelId);
      }
      this.form = this.buildFormGroupFromElements(
        this.submodel.submodelElements ?? [],
      );
      this.passFound = true;
    } catch {
      this.passFound = false;
      await this.toastService.show({
        message: this.translate.instant(
          'dpp.passport_detail_view.load_error_message',
        ),
        type: 'error',
      });
    } finally {
      this.loading = false;
    }
  }

  /**
   * Recursively builds a FormGroup from a list of SubmodelElements.
   * Each element's idShort becomes a key in the group.
   */
  buildFormGroupFromElements(elements: SubmodelElement[]): FormGroup {
    const controls: Record<string, SubmodelFormControl> = {};
    for (const element of elements) {
      const key = element.idShort ?? '';
      controls[key] = this.buildControlForElement(element);
    }
    return this.fb.group(controls);
  }

  private buildControlForElement(
    element: SubmodelElement,
  ): SubmodelFormControl {
    switch (element.modelType) {
      case SubmodelElementTypes.Property: {
        const prop = element as Property;
        return this.fb.control(prop.value ?? '');
      }
      case SubmodelElementTypes.File: {
        const file = element as File;
        return this.fb.control(file.value ?? '');
      }
      case SubmodelElementTypes.MultiLanguageProperty: {
        const mlProp = element as MultiLanguageProperty;
        const langControls: Record<string, FormControl> = {};
        for (const langString of mlProp.value ?? []) {
          langControls[langString.language] = this.fb.control(
            langString.text ?? '',
          );
        }
        return this.fb.group(langControls);
      }
      case SubmodelElementTypes.SubmodelElementCollection: {
        const collection = element as SubmodelElementCollection;
        return this.buildFormGroupFromElements(collection.value ?? []);
      }
      case SubmodelElementTypes.SubmodelElementList: {
        if (this.isCreateMode) {
          return this.fb.array([]);
        }
        const list = element as SubmodelElementList;
        return this.fb.array(
          (list.value ?? []).map((item) => this.buildControlForElement(item)),
        );
      }
      default:
        return this.fb.control('');
    }
  }

  getFormControl(key: string): FormControl {
    return this.form.get(key) as FormControl;
  }

  async onSave(): Promise<void> {
    if (!this.submodel) {
      return;
    }
    this.saving = true;
    try {
      const updatedSubmodel = this.applyFormToSubmodel(
        this.submodel,
        this.form,
      );
      if (this.isCreateMode) {
        const created = await this.dppService.createSubmodel(
          this.dppId,
          updatedSubmodel,
        );
        this.submodelId = created.id;
      } else {
        await this.dppService.updateSubmodel(updatedSubmodel);
      }
      this.saving = false;
      this.toastService.show({
        message: this.translate.instant(
          'dpp.generic_submodel_editor.save_success',
        ),
        type: 'success',
      });
      this.navigateToPassportDetail();
    } catch (error: unknown) {
      this.saving = false;
      throw error;
    }
  }

  private applyFormToSubmodel(
    submodel: Submodel,
    formGroup: FormGroup,
  ): Submodel {
    return {
      ...submodel,
      submodelElements: this.applyFormToElements(
        submodel.submodelElements ?? [],
        formGroup,
      ),
    };
  }

  private applyFormToElements(
    elements: SubmodelElement[],
    formGroup: FormGroup,
  ): SubmodelElement[] {
    return elements.map((element) => {
      const key = element.idShort ?? '';
      const control = formGroup.get(key);
      if (!control) {
        return element;
      }
      return this.applyControlToElement(element, control);
    });
  }

  private applyControlToElement(
    element: SubmodelElement,
    control: AbstractControl,
  ): SubmodelElement {
    switch (element.modelType) {
      case SubmodelElementTypes.Property: {
        if (!control.value) {
          const {value: _, ...rest} = element as Property;
          return rest as Property;
        }
        return {...element, value: control.value} as Property;
      }
      case SubmodelElementTypes.File: {
        if (!control.value) {
          const {value: _, ...rest} = element as File;
          return rest as File;
        }
        return {...element, value: control.value} as File;
      }
      case SubmodelElementTypes.MultiLanguageProperty: {
        const langGroup = control as FormGroup;
        const updatedValue: LangString[] = Object.entries(
          langGroup.controls,
        ).map(([language, ctrl]) => ({
          language,
          text: ctrl.value ?? '',
        }));
        if (updatedValue.length === 0) {
          const {value: _, ...rest} = element as MultiLanguageProperty;
          return rest as MultiLanguageProperty;
        }
        return {...element, value: updatedValue} as MultiLanguageProperty;
      }
      case SubmodelElementTypes.SubmodelElementCollection: {
        const collection = element as SubmodelElementCollection;
        const nestedGroup = control as FormGroup;
        return {
          ...element,
          value: this.applyFormToElements(collection.value ?? [], nestedGroup),
        } as SubmodelElementCollection;
      }
      case SubmodelElementTypes.SubmodelElementList: {
        const list = element as SubmodelElementList;
        const formArray = control as FormArray;
        const templateValues = list.value ?? [];
        const updatedItems = formArray.controls.map((ctrl, index) => {
          const templateItem = templateValues[index] ?? templateValues[0];
          if (!templateItem) {
            return ctrl.value;
          }
          return this.applyControlToElement(templateItem, ctrl);
        });
        if (updatedItems.length === 0) {
          return element;
        }
        return {...element, value: updatedItems} as SubmodelElementList;
      }
      default:
        return element;
    }
  }

  async onCancel(): Promise<void> {
    const instance = await this.modalService.open({
      content: this.cancelModalRef,
      centered: true,
      data: this.submodelName,
    });
    instance.onClose.on((shouldNavigateBack: boolean) => {
      if (shouldNavigateBack) {
        this.navigateToPassportDetail();
      }
    });
  }

  navigateToPassportList(): void {
    this.router.navigate(['/app/dpp/list-passports']);
  }

  navigateToPassportDetail(): void {
    this.router.navigate(['/app/dpp/view-passport', this.dppId]);
  }
}
